/**
 * History & Analytics Page
 *
 * Layout (sessions-first):
 *   1. Header + summary tiles
 *   2. Filter pills
 *   3. Session list (chronological, expand/collapse detail)
 *   4. Analytics section (collapsible) — PRs, charts, 1RM explorer
 */

import { useState, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { calc1RM } from '@/hooks/use1RM';
import LineChart from '@/components/charts/LineChart';
import VolumeHeatmap from '@/components/charts/VolumeHeatmap';
import TonnageChart from '@/components/charts/TonnageChart';
import MuscleBalance from '@/components/charts/MuscleBalance';
import MuscleVolumeChart from '@/components/charts/MuscleVolumeChart';
import BodyweightLog from '@/components/BodyweightLog';
import MonthlyCalendar from '@/components/MonthlyCalendar';
import { SkeletonCard, SkeletonStatRow } from '@/components/Skeleton';
import type { WorkoutSession, LoggedSet, Exercise } from '@/types';

// ── Filter types ─────────────────────────────────────────────

type Filter = 'all' | 'push' | 'pull' | 'legs' | 'upper' | 'lower' | 'week' | 'month';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all',   label: 'All' },
  { id: 'push',  label: 'Push' },
  { id: 'pull',  label: 'Pull' },
  { id: 'legs',  label: 'Legs' },
  { id: 'upper', label: 'Upper' },
  { id: 'lower', label: 'Lower' },
  { id: 'week',  label: 'This Week' },
  { id: 'month', label: 'This Month' },
];

const PUSH_DAYS = new Set(['push', 'anterior']);
const PULL_DAYS = new Set(['pull', 'posterior']);

function applyFilter(sessions: WorkoutSession[], filter: Filter): WorkoutSession[] {
  const now = Date.now();
  return sessions.filter((s) => {
    if (filter === 'push')  return PUSH_DAYS.has(s.splitDay);
    if (filter === 'pull')  return PULL_DAYS.has(s.splitDay);
    if (filter === 'legs')  return s.splitDay === 'legs';
    if (filter === 'upper') return s.splitDay === 'upper';
    if (filter === 'lower') return s.splitDay === 'lower';
    if (filter === 'week')  return now - new Date(s.startedAt).getTime() < 7  * 86_400_000;
    if (filter === 'month') return now - new Date(s.startedAt).getTime() < 30 * 86_400_000;
    return true;
  });
}

function splitDayLabel(day: string): string {
  const MAP: Record<string, string> = {
    push: 'Push', anterior: 'Push',
    pull: 'Pull', posterior: 'Pull',
    legs: 'Legs', upper: 'Upper', lower: 'Lower', full_body_day: 'Full Body',
  };
  return MAP[day] ?? day;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

function fmtDuration(seconds?: number): string {
  if (!seconds) return '—';
  return `${Math.floor(seconds / 60)} min`;
}

// ── Session Row ───────────────────────────────────────────────

function SessionRow({ session }: { session: WorkoutSession }) {
  const [expanded, setExpanded] = useState(false);

  const sets = useLiveQuery(
    () => expanded
      ? db.loggedSets.where('workoutId').equals(session.id).toArray()
      : Promise.resolve([] as LoggedSet[]),
    [session.id, expanded],
  ) ?? [];

  const byExercise = useMemo(() => {
    const map = new Map<string, LoggedSet[]>();
    for (const s of sets) {
      const arr = map.get(s.exerciseId) ?? [];
      arr.push(s);
      map.set(s.exerciseId, arr);
    }
    return map;
  }, [sets]);

  const exerciseIds = [...byExercise.keys()];
  const exercises = useLiveQuery(
    () => exerciseIds.length > 0
      ? db.exercises.bulkGet(exerciseIds)
      : Promise.resolve([] as (Exercise | undefined)[]),
    [exerciseIds.join(',')],
  ) ?? [];
  const exMap = new Map((exercises.filter(Boolean) as Exercise[]).map((e) => [e.id, e]));

  const hardSetCount  = sets.filter((s) => !s.isWarmup && s.rpe >= 7).length;
  const totalSetCount = sets.filter((s) => !s.isWarmup).length;

  const splitLabel = splitDayLabel(session.splitDay);
  const isPush = PUSH_DAYS.has(session.splitDay);
  const splitColor = isPush ? 'badge-accent' : 'bg-success/20 text-success';
  const statusColor =
    session.status === 'completed' ? 'text-success' :
    session.status === 'abandoned' ? 'text-danger'  : 'text-warning';

  return (
    <div className="card space-y-0 overflow-hidden">
      <button
        className="w-full flex items-center gap-3 text-left py-1 active:opacity-70 transition-opacity"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`badge text-xs ${splitColor}`}>{splitLabel}</span>
            <span className={`text-xs font-medium ${statusColor}`}>{session.status}</span>
          </div>
          <p className="font-semibold text-ink text-sm">{fmtDate(session.startedAt)}</p>
        </div>

        <div className="flex gap-4 text-right flex-shrink-0">
          <div>
            <p className="text-xs font-bold text-ink">{fmtDuration(session.durationSeconds)}</p>
            <p className="text-xs text-ink-faint">duration</p>
          </div>
          <div>
            <p className="text-xs font-bold text-ink">
              {expanded ? (totalSetCount || '…') : '—'}
            </p>
            <p className="text-xs text-ink-faint">sets</p>
          </div>
        </div>

        <span className={`text-ink-faint text-sm transition-transform duration-200
                          ${expanded ? 'rotate-180' : ''}`}>›</span>
      </button>

      {expanded && sets.length > 0 && (
        <div className="border-t border-surface-400 pt-3 mt-2 space-y-3 animate-fade-in">
          {[...byExercise.entries()].map(([exId, exSets]) => {
            const ex = exMap.get(exId);
            const workingSets = exSets.filter((s) => !s.isWarmup);
            const best1RM = Math.max(...workingSets.map((s) => calc1RM(s.weightLbs, s.reps)));

            return (
              <div key={exId} className="bg-surface-200 rounded-xl px-3 py-2.5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-semibold text-ink text-sm leading-tight">
                    {ex?.name ?? 'Unknown Exercise'}
                  </p>
                  {best1RM > 0 && (
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-ink-faint">Est. 1RM</p>
                      <p className="text-sm font-bold text-accent">{Math.round(best1RM)} lbs</p>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  {workingSets.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 text-xs text-ink-muted font-mono">
                      <span className="w-4 text-ink-faint">S{s.setNumber}</span>
                      <span className="text-ink">{s.weightLbs} lbs</span>
                      <span className="text-ink-faint">×</span>
                      <span className="text-ink">{s.reps} reps</span>
                      <span className={`ml-auto font-semibold ${
                        s.rpe >= 9 ? 'text-danger' : s.rpe >= 7 ? 'text-warning' : 'text-success'
                      }`}>RPE {s.rpe}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {hardSetCount > 0 && (
            <p className="text-xs text-ink-faint px-1">
              {hardSetCount} hard sets (RPE ≥ 7)
            </p>
          )}
        </div>
      )}

      {expanded && sets.length === 0 && (
        <div className="border-t border-surface-400 pt-3 mt-2 animate-fade-in">
          <p className="text-xs text-ink-faint text-center py-2">No sets logged</p>
        </div>
      )}
    </div>
  );
}

// ── Analytics section (collapsible) ──────────────────────────

function OneRMExplorer() {
  const [selectedId, setSelectedId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const exercisesWithHistory = useLiveQuery(async () => {
    const setIds = await db.loggedSets.filter((s) => !s.isWarmup).toArray();
    const uniqueIds = [...new Set(setIds.map((s) => s.exerciseId))];
    const exercises = await db.exercises.bulkGet(uniqueIds);
    return (exercises.filter(Boolean) as Exercise[]).sort((a, b) => a.name.localeCompare(b.name));
  }, []) ?? [];

  const chartData = useLiveQuery(async () => {
    if (!selectedId) return [];
    const sets = await db.loggedSets
      .where('exerciseId').equals(selectedId)
      .filter((s) => !s.isWarmup)
      .sortBy('loggedAt');

    const byDate = new Map<string, number>();
    for (const s of sets) {
      const date = s.loggedAt.slice(0, 10);
      const e1rm = calc1RM(s.weightLbs, s.reps);
      byDate.set(date, Math.max(byDate.get(date) ?? 0, e1rm));
    }
    return Array.from(byDate.entries())
      .map(([date, value]) => ({ date, value: Math.round(value * 10) / 10 }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [selectedId]) ?? [];

  const selectedExercise = exercisesWithHistory.find((e) => e.id === selectedId);
  const filtered = exercisesWithHistory.filter((e) =>
    e.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (exercisesWithHistory.length === 0) return null;

  return (
    <section>
      <p className="section-header">1RM Progression</p>
      <div className="card space-y-4">
        {/* Search + exercise picker */}
        <input
          type="text"
          placeholder="Search exercises…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-surface-300 rounded-xl px-3 py-3 text-sm text-ink
                     focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {searchQuery && (
          <div className="space-y-1 max-h-48 overflow-y-auto -mt-2">
            {filtered.map((ex) => (
              <button
                key={ex.id}
                onClick={() => { setSelectedId(ex.id); setSearchQuery(''); }}
                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors
                            ${selectedId === ex.id
                              ? 'bg-accent/10 text-accent font-semibold'
                              : 'hover:bg-surface-300 text-ink'}`}
              >
                {ex.name}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-ink-faint text-center py-3">No matches</p>
            )}
          </div>
        )}

        {selectedId && (
          <div className="flex items-center gap-2 px-1">
            <span className="text-sm font-semibold text-ink flex-1">{selectedExercise?.name}</span>
            <button
              onClick={() => setSelectedId('')}
              className="text-xs text-ink-faint hover:text-ink-muted"
            >✕ clear</button>
          </div>
        )}

        {selectedId && chartData.length > 0 && (
          <div className="animate-fade-in">
            <LineChart
              data={chartData}
              label={selectedExercise?.name}
              unit="lbs"
              color="#7c3aed"
            />
            <div className="flex justify-between items-center mt-3 px-1">
              <div>
                <p className="text-xs text-ink-faint">All-time best est. 1RM</p>
                <p className="text-xl font-bold text-accent font-mono">
                  {Math.round(Math.max(...chartData.map((d) => d.value)))} lbs
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-ink-faint">Data points</p>
                <p className="text-xl font-bold text-ink font-mono">{chartData.length}</p>
              </div>
            </div>
          </div>
        )}

        {selectedId && chartData.length === 0 && (
          <p className="text-sm text-ink-faint text-center py-4">No working sets logged yet</p>
        )}
      </div>
    </section>
  );
}

function PersonalRecordsSection() {
  const records = useLiveQuery(async () => {
    const prs = await db.personalRecords.orderBy('achievedAt').reverse().toArray();
    const best = new Map<string, typeof prs[0]>();
    for (const pr of prs) {
      const existing = best.get(pr.exerciseId);
      if (!existing || pr.new1RM > existing.new1RM) best.set(pr.exerciseId, pr);
    }
    return [...best.values()].sort((a, b) => b.new1RM - a.new1RM);
  }, []) ?? [];

  if (records.length === 0) return null;

  return (
    <section>
      <p className="section-header">Personal Records</p>
      <div className="card space-y-0">
        {records.map((pr, i) => (
          <div key={pr.id}
            className={`flex items-center gap-3 py-3 ${i < records.length - 1 ? 'border-b border-surface-400' : ''}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center
                             text-xs font-bold flex-shrink-0
                             ${i === 0 ? 'bg-warning/20 text-warning' :
                               i === 1 ? 'bg-surface-400 text-ink-muted' :
                               i === 2 ? 'bg-accent/10 text-accent' :
                               'bg-surface-300 text-ink-faint'}`}>
              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-ink text-sm leading-tight truncate">
                {pr.exerciseName}
              </p>
              <p className="text-xs text-ink-muted mt-0.5">
                {pr.weightLbs} lbs × {pr.reps} reps · {fmtDate(pr.achievedAt)}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-base font-bold text-accent">{Math.round(pr.new1RM)}</p>
              <p className="text-xs text-ink-faint">est. 1RM</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function WeeklyVolumeChart() {
  const weeklyData = useLiveQuery(async () => {
    const sets = await db.loggedSets
      .where('loggedAt').aboveOrEqual(new Date(Date.now() - 56 * 86_400_000).toISOString())
      .filter((s) => !s.isWarmup && s.rpe >= 7)
      .toArray();

    const getWeekKey = (iso: string) => {
      const d = new Date(iso);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(d.setDate(diff));
      return monday.toISOString().slice(0, 10);
    };

    const byWeek = new Map<string, number>();
    for (const s of sets) {
      const k = getWeekKey(s.loggedAt);
      byWeek.set(k, (byWeek.get(k) ?? 0) + 1);
    }

    return Array.from(byWeek.entries())
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-8);
  }, []) ?? [];

  if (weeklyData.length < 2) return null;

  return (
    <section>
      <p className="section-header">Weekly Hard Sets (8 Weeks)</p>
      <div className="card">
        <LineChart data={weeklyData} unit="sets" color="#22c55e" height={120} />
      </div>
    </section>
  );
}

function AnalyticsSection() {
  const [open, setOpen] = useState(false);

  return (
    <section>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full mb-3"
      >
        <p className="section-header mb-0">Analytics</p>
        <span className={`text-ink-faint text-sm transition-transform duration-200
                          ${open ? 'rotate-90' : ''}`}>›</span>
      </button>

      {open && (
        <div className="space-y-6 animate-fade-in">
          <BodyweightLog />
          <MuscleVolumeChart />
          <PersonalRecordsSection />
          <VolumeHeatmap />
          <TonnageChart />
          <MuscleBalance />
          <WeeklyVolumeChart />
          <OneRMExplorer />
        </div>
      )}

      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="w-full card text-center py-4 text-sm text-ink-muted
                     hover:bg-surface-200 transition-colors"
        >
          PRs · Volume · 1RM Progression · Muscle Balance →
        </button>
      )}
    </section>
  );
}

// ── Main Page ─────────────────────────────────────────────────

export default function HistoryPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const [calendarDay, setCalendarDay] = useState<string | null>(null);

  const allSessionsRaw = useLiveQuery(
    () => db.workouts.orderBy('startedAt').reverse().toArray(), []
  );
  const isLoading   = allSessionsRaw === undefined;
  const allSessions = allSessionsRaw ?? [];

  const filtered = useMemo(() => {
    let sessions = applyFilter(allSessions, filter);
    if (calendarDay) {
      sessions = sessions.filter((s) => s.startedAt.slice(0, 10) === calendarDay);
    }
    return sessions;
  }, [allSessions, filter, calendarDay]);

  const completedCount = allSessions.filter((s) => s.status === 'completed').length;
  const pushCount = allSessions.filter((s) => PUSH_DAYS.has(s.splitDay) && s.status === 'completed').length;
  const pullCount = allSessions.filter((s) => PULL_DAYS.has(s.splitDay) && s.status === 'completed').length;
  const legsCount = allSessions.filter((s) => s.splitDay === 'legs' && s.status === 'completed').length;

  return (
    <div className="content-pad-bottom px-4 pt-6 pb-4 space-y-6 animate-fade-in">

      {/* Header */}
      <header>
        <p className="text-ink-muted text-sm">Performance</p>
        <h1 className="text-2xl font-bold text-ink tracking-tight">History</h1>
      </header>

      {isLoading && (
        <div className="space-y-4 animate-fade-in">
          <SkeletonStatRow />
          <SkeletonCard rows={2} />
          <SkeletonCard rows={4} />
        </div>
      )}

      {/* Summary tiles */}
      {!isLoading && completedCount > 0 && (
        <div className="flex gap-3">
          {[
            { label: 'Total', value: String(completedCount) },
            { label: 'Push',  value: String(pushCount) },
            { label: 'Pull',  value: String(pullCount) },
            ...(legsCount > 0 ? [{ label: 'Legs', value: String(legsCount) }] : []),
          ].map(({ label, value }) => (
            <div key={label} className="flex-1 card-elevated text-center py-3">
              <p className="text-xl font-bold text-ink">{value}</p>
              <p className="text-xs text-ink-muted mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Calendar ── */}
      {!isLoading && completedCount > 0 && (
        <MonthlyCalendar onDaySelect={setCalendarDay} selectedDay={calendarDay} />
      )}

      {/* ── Sessions (always first) ── */}
      {!isLoading && (
        <section>
          {/* Filter pills */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-3">
            {FILTERS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setFilter(id)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium
                           transition-colors duration-150 min-h-tap
                           ${filter === id
                             ? 'bg-accent text-white'
                             : 'bg-surface-300 text-ink-muted hover:text-ink'}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-3">
            <p className="section-header mb-0">Sessions</p>
            <span className="text-xs text-ink-faint">{filtered.length} shown</span>
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center space-y-3">
              <div className="w-16 h-16 rounded-2xl bg-surface-300 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-8 h-8 text-ink-faint" fill="none"
                  stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                </svg>
              </div>
              <p className="font-semibold text-ink">No sessions yet</p>
              <p className="text-ink-muted text-sm">
                {filter === 'all'
                  ? 'Complete your first workout to see data here.'
                  : `No ${filter} sessions found.`}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((s) => (
                <SessionRow key={s.id} session={s} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Analytics (collapsible, below sessions) ── */}
      {!isLoading && completedCount > 0 && <AnalyticsSection />}

    </div>
  );
}
